/**
 * UI MANAGER - Gestor de Interfaz de Usuario
 * Maneja el HUD y elementos de UI del juego
 */

import { formatTime, getIntegrityColor } from "../utils/helpers.js";
import { CONTROL_BUTTONS } from "../utils/constants.js";

export default class UIManager {
  constructor(scene) {
    this.scene = scene;
    this.elements = {};
    this.controlButtons = {};
  }

  /**
   * Crear HUD completo
   */
  createHUD() {
    const width = this.scene.cameras.main.width;

    // Fondo del HUD
    this.elements.hudBg = this.scene.add.rectangle(
      width / 2,
      30,
      width - 20,
      50,
      0xffffff,
      0.9,
    );

    // Votos
    this.elements.votesText = this.scene.add.text(20, 20, "🗳️ 20/20", {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#2d3748",
      fontFamily: "Arial",
    });

    // Timer
    this.elements.timerText = this.scene.add.text(width / 2, 20, "⏱️ 60s", {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#2d3748",
      fontFamily: "Arial",
    });
    this.elements.timerText.setOrigin(0.5, 0);

    // Barra de integridad
    this.createIntegrityBar(width - 40, 80);

    // Ciclo/Ronda
    this.elements.cycleText = this.scene.add.text(
      width / 2,
      50,
      "CICLO: 2026-2030",
      {
        fontSize: "14px",
        color: "#4a5568",
        fontFamily: "Arial",
      },
    );
    this.elements.cycleText.setOrigin(0.5, 0);

    // ========================================
    // 🎮 BOTONES DE CONTROL
    // ========================================
    this.createControlButtons();
  }

  createControlButtons() {
    const width = this.scene.cameras.main.width;

    // BOTÓN MUTE
    this.controlButtons.mute = this.createButton(
      width + CONTROL_BUTTONS.MUTE.x,
      CONTROL_BUTTONS.MUTE.y,
      CONTROL_BUTTONS.MUTE.width,
      CONTROL_BUTTONS.MUTE.height,
      this.scene.audioManager?.getMuteState()
        ? CONTROL_BUTTONS.MUTE.iconMuted
        : CONTROL_BUTTONS.MUTE.iconUnmuted,
      CONTROL_BUTTONS.MUTE.color,
      CONTROL_BUTTONS.MUTE.hoverColor,
      () => this.onMuteClicked(),
    );

    // BOTÓN PAUSE
    this.controlButtons.pause = this.createButton(
      width + CONTROL_BUTTONS.PAUSE.x,
      CONTROL_BUTTONS.PAUSE.y,
      CONTROL_BUTTONS.PAUSE.width,
      CONTROL_BUTTONS.PAUSE.height,
      CONTROL_BUTTONS.PAUSE.icon,
      CONTROL_BUTTONS.PAUSE.color,
      CONTROL_BUTTONS.PAUSE.hoverColor,
      () => this.onPauseClicked(),
    );

    // BOTÓN QUIT
    this.controlButtons.quit = this.createButton(
      width + CONTROL_BUTTONS.QUIT.x,
      CONTROL_BUTTONS.QUIT.y,
      CONTROL_BUTTONS.QUIT.width,
      CONTROL_BUTTONS.QUIT.height,
      CONTROL_BUTTONS.QUIT.icon,
      CONTROL_BUTTONS.QUIT.color,
      CONTROL_BUTTONS.QUIT.hoverColor,
      () => this.onQuitClicked(),
    );

    console.log("🎮 Botones de control creados");
  }

  /**
   * Crear un botón genérico
   */
  createButton(x, y, width, height, icon, color, hoverColor, callback) {
    // Fondo del botón
    const bg = this.scene.add.rectangle(x, y, width, height, color, 0.9);
    bg.setOrigin(1, 0); // Origen top-right
    bg.setStrokeStyle(2, 0x2d3748);
    bg.setInteractive({ useHandCursor: true });

    // Ícono
    const iconText = this.scene.add.text(x - width / 2, y + height / 2, icon, {
      fontSize: "24px",
    });
    iconText.setOrigin(0.5);

    // Eventos
    bg.on("pointerover", () => {
      bg.setFillStyle(hoverColor, 1);
      this.scene.tweens.add({
        targets: [bg, iconText],
        scale: 1.1,
        duration: 100,
      });
    });

    bg.on("pointerout", () => {
      bg.setFillStyle(color, 0.9);
      this.scene.tweens.add({
        targets: [bg, iconText],
        scale: 1.0,
        duration: 100,
      });
    });

    bg.on("pointerdown", callback);

    return { bg, iconText };
  }

  /**
   * Handler: Botón MUTE
   */
  onMuteClicked() {
    if (!this.scene.audioManager) return;

    const isMuted = this.scene.audioManager.toggleMute();

    // Actualizar ícono
    const newIcon = isMuted
      ? CONTROL_BUTTONS.MUTE.iconMuted
      : CONTROL_BUTTONS.MUTE.iconUnmuted;

    this.controlButtons.mute.iconText.setText(newIcon);

    console.log(`🔊 Mute toggled: ${isMuted}`);
  }

  /**
   * Handler: Botón PAUSE
   */
  onPauseClicked() {
    if (this.scene.pauseGame) {
      this.scene.pauseGame();
    }
  }

  /**
   * Handler: Botón QUIT
   */
  onQuitClicked() {
    if (this.scene.showQuitConfirmation) {
      this.scene.showQuitConfirmation();
    }
  }

  /**
   * Actualizar ícono de mute (llamado externamente si cambia el estado)
   */
  updateMuteIcon(isMuted) {
    if (this.controlButtons.mute) {
      const newIcon = isMuted
        ? CONTROL_BUTTONS.MUTE.iconMuted
        : CONTROL_BUTTONS.MUTE.iconUnmuted;

      this.controlButtons.mute.iconText.setText(newIcon);
    }
  }

  /**
   * Crear barra de integridad
   */
  createIntegrityBar(x, y) {
    const barWidth = 25;
    const barHeight = 150;

    // Fondo de la barra
    this.elements.integrityBg = this.scene.add.rectangle(
      x,
      y,
      barWidth,
      barHeight,
      0xe2e8f0,
    );
    this.elements.integrityBg.setOrigin(0, 0);
    this.elements.integrityBg.setStrokeStyle(2, 0x2d3748);

    // Barra de progreso
    this.elements.integrityBar = this.scene.add.rectangle(
      x + 2,
      y + 2,
      barWidth - 4,
      barHeight - 4,
      0x48bb78, // ← Cambió "y + 2" en lugar de "y + barHeight - 4"
    );
    this.elements.integrityBar.setOrigin(0, 0);

    // Texto de porcentaje (arriba de la barra)
    this.elements.integrityText = this.scene.add.text(
      x + barWidth / 2,
      y - 10,
      "100%",
      {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#2d3748",
        fontFamily: "Arial",
      },
    );
    this.elements.integrityText.setOrigin(0.5);
  }

  /**
   * Actualizar votos en HUD
   * @param {number} current - Votos actuales
   * @param {number} total - Votos totales
   */
  updateVotes(current, total) {
    if (this.elements.votesText) {
      this.elements.votesText.setText(`🗳️ ${current}/${total}`);

      // Cambiar color si quedan pocos votos
      if (current <= 5) {
        this.elements.votesText.setColor("#dc2626");
      } else {
        this.elements.votesText.setColor("#2d3748");
      }
    }
  }

  /**
   * Actualizar timer en HUD
   * @param {number} timeLeft - Segundos restantes
   */
  updateTimer(timeLeft) {
    if (this.elements.timerText) {
      this.elements.timerText.setText(`⏱️ ${formatTime(timeLeft)}`);

      // Cambiar color si queda poco tiempo
      if (timeLeft <= 10) {
        this.elements.timerText.setColor("#dc2626");

        // Parpadeo en últimos 5 segundos
        if (timeLeft <= 5) {
          this.scene.tweens.add({
            targets: this.elements.timerText,
            alpha: 0.3,
            duration: 500,
            yoyo: true,
          });
        }
      } else {
        this.elements.timerText.setColor("#2d3748");
        this.elements.timerText.setAlpha(1);
      }
    }
  }

  /**
   * Actualizar barra de integridad
   * @param {number} integrity - Porcentaje 0-100
   */
  updateIntegrity(integrity) {
    if (this.elements.integrityBar && this.elements.integrityText) {
      // Actualizar ALTURA de la barra
      const maxHeight = 146; // 150 - 4 (padding)
      const newHeight = (maxHeight * integrity) / 100;

      // Calcular nueva posición Y (para que se vacíe desde arriba)
      const integrityBgY = this.elements.integrityBg.y;
      const newY = integrityBgY + 2 + (maxHeight - newHeight);

      // Actualizar posición y altura
      this.elements.integrityBar.y = newY;
      this.elements.integrityBar.height = Math.max(0, newHeight);

      // Actualizar texto
      this.elements.integrityText.setText(`${Math.round(integrity)}%`);

      // Cambiar color según nivel
      const color = getIntegrityColor(integrity);
      this.elements.integrityBar.setFillStyle(
        parseInt(color.replace("#", ""), 16),
      );

      // Efecto de pulso si está baja
      if (integrity < 40) {
        this.scene.tweens.add({
          targets: this.elements.integrityBar,
          alpha: 0.6,
          duration: 300,
          yoyo: true,
        });
      }
    }
  }

  /**
   * Actualizar ciclo/ronda
   * @param {string} cycleText - Texto del ciclo (ej: "2026-2030")
   */
  updateCycle(cycleText) {
    if (this.elements.cycleText) {
      this.elements.cycleText.setText(`CICLO: ${cycleText}`);
    }
  }

  /**
   * Mostrar mensaje flotante temporal
   * @param {string} text - Texto del mensaje
   * @param {number} x - Posición X
   * @param {number} y - Posición Y
   * @param {string} color - Color del texto
   */
  showFloatingMessage(text, x, y, color = "#48bb78") {
    const message = this.scene.add.text(x, y, text, {
      fontSize: "20px",
      fontWeight: "bold",
      color: color,
      fontFamily: "Arial",
      stroke: "#000000",
      strokeThickness: 3,
    });
    message.setOrigin(0.5);

    // Animación: flotar y desaparecer
    this.scene.tweens.add({
      targets: message,
      y: y - 50,
      alpha: 0,
      duration: 1000,
      ease: "Cubic.easeOut",
      onComplete: () => {
        message.destroy();
      },
    });
  }

  /**
   * Ocultar HUD
   */
  hideHUD() {
    Object.values(this.elements).forEach((element) => {
      if (element && element.setVisible) {
        element.setVisible(false);
      }
    });

    // Ocultar botones también
    Object.values(this.controlButtons).forEach((button) => {
      if (button.bg) button.bg.setVisible(false);
      if (button.iconText) button.iconText.setVisible(false);
    });
  }

  /**
   * Mostrar HUD
   */
  showHUD() {
    Object.values(this.elements).forEach((element) => {
      if (element && element.setVisible) {
        element.setVisible(true);
      }
    });

    // Mostrar botones también
    Object.values(this.controlButtons).forEach((button) => {
      if (button.bg) button.bg.setVisible(true);
      if (button.iconText) button.iconText.setVisible(true);
    });
  }

  /**
   * Destruir el UI Manager
   */
  destroy() {
    Object.values(this.elements).forEach((element) => {
      if (element && element.destroy) {
        element.destroy();
      }
    });

    // Destruir botones
    Object.values(this.controlButtons).forEach((button) => {
      if (button.bg) button.bg.destroy();
      if (button.iconText) button.iconText.destroy();
    });

    this.elements = {};
    this.controlButtons = {};
    this.scene = null;
  }
}
